<template>
  <span
    class="inline-flex size-8 shrink-0 items-center justify-center rounded-md"
    :class="logo.tile"
    aria-hidden="true"
  >
    <fa-icon v-if="logo.kind === 'fa'" :icon="logo.icon" :class="logo.glyph" />
    <component :is="logo.component" v-else :class="logo.glyph" />
  </span>
</template>

<script setup lang="ts">
import type { Component } from 'vue';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faAlipay,
  faCcAmex,
  faCcDiscover,
  faCcVisa,
  faPaypal,
  faWeixin,
} from '@fortawesome/free-brands-svg-icons';
import { faCreditCard, faWallet } from '@fortawesome/pro-regular-svg-icons';
import KlarnaIcon from '~/assets/icons/klarna.svg?component';
import MastercardIcon from '~/assets/icons/mastercard.svg?component';

// A payment method's logo as a small square badge in the brand's own colours,
// the shape chargebee's payment form uses for its method rows (the Klarna "K."
// badge), so saved and new methods read alike. `type` is
// Chargebee's payment_source.type ("card", "paypal_express_checkout",
// "klarna", ...) or "balance" for the account balance; `brand` is the card
// scheme for a card ("visa", "mastercard", ...).
const props = defineProps<{
  type: string;
  brand?: string;
}>();

type Logo = { tile: string; glyph: string } & (
  | { kind: 'fa'; icon: IconDefinition }
  | { kind: 'component'; component: Component }
);

const fa = (icon: IconDefinition, tile: string, glyph: string): Logo => ({
  kind: 'fa',
  icon,
  tile,
  glyph,
});
const svg = (component: Component, tile: string, glyph: string): Logo => ({
  kind: 'component',
  component,
  tile,
  glyph,
});

// Unknown types and schemes: a neutral tile in the site's own colours.
const NEUTRAL = fa(faCreditCard, 'bg-depth-3', 'size-4 text-mouse');

function cardLogo(brand: string | undefined): Logo {
  switch (brand) {
    case 'visa':
      return fa(faCcVisa, 'bg-white', 'size-6 text-pay-visa');
    case 'mastercard':
      return svg(MastercardIcon, 'bg-white', 'h-4 w-6');
    case 'american_express':
      return fa(faCcAmex, 'bg-pay-amex', 'size-6 text-white');
    case 'discover':
      return fa(faCcDiscover, 'bg-white', 'size-6 text-pay-discover');
    default:
      return NEUTRAL;
  }
}

const logo = computed((): Logo => {
  switch (props.type) {
    case 'balance':
      return fa(faWallet, 'bg-brand/10', 'size-4 text-brand');
    case 'card':
      return cardLogo(props.brand);
    case 'paypal_express_checkout':
      return fa(faPaypal, 'bg-white', 'size-4 text-pay-paypal');
    case 'klarna':
    case 'klarna_pay_now':
      return svg(KlarnaIcon, 'bg-pay-klarna', 'size-3.5 text-pay-klarna-ink');
    case 'alipay':
      return fa(faAlipay, 'bg-white', 'size-4 text-pay-alipay');
    case 'wechat_pay':
      return fa(faWeixin, 'bg-pay-wechat', 'size-4 text-white');
    default:
      return NEUTRAL;
  }
});
</script>
